import { useState, useEffect } from "react";
import {
  doc,
  getDoc,
  setDoc,
  collection,
  getDocs,
  updateDoc,
} from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { firestore } from "../../../firebase";
import { v4 as uuidv4 } from "uuid";
import Stripe from "stripe";
import { normalizeLang } from "../utils/tagTranslation";
import { buildTagsLang } from "../utils/buildTagsLang";
import { translateBatch } from "../utils/translateBatch";

// Same test key editMyLocation.js already uses for subscription-quantity
// adjustments on activate/deactivate.
const stripe = Stripe(
  "sk_test_51OC6QGF698POdFrNomZgR5tNBvaWTSnrpzevHl1YXKIBfiB5qaU79YeV2GuCBBKZgdhKZdWapxKqqfa2l0ATsPlf00FW6qVCHg",
);

const db = firestore;
const storage = getStorage();

// Mirrors editMyLocation.js's state block, save/publish, and activate/deactivate
// logic, but keyed by an explicit locationId instead of useParams() so it can be
// reused for a location other than the one in the current URL.
export function useLocationEditor({ locationId, currentBusinessID }) {
  const [regularOpeningHours, setRegularOpeningHours] = useState({
    mon: null,
    tue: null,
    wed: null,
    thu: null,
    fri: null,
    sat: null,
    sun: null,
  });
  const [regularClosingHours, setRegularClosingHours] = useState({
    mon: null,
    tue: null,
    wed: null,
    thu: null,
    fri: null,
    sat: null,
    sun: null,
  });
  const [newCustomOpeningHours, setNewCustomOpeningHours] = useState({
    date: null,
    open: null,
    close: null,
  });
  const [locationInformation, setLocationinformation] = useState(null);
  const [newProfileImage, setNewProfileImage] = useState("");
  const [businessOwner, setBusinessOwner] = useState(false);
  const [textInformation, setTextInformation] = useState({
    name: "",
    tags: [],
    adress: "",
    description: "",
    websiteLink: "",
    bookingLink: "",
    phoneLink: "",
    tags_enUS: [],
    description_enUS: "",
    locationTags: [],
    locationTags_enUS: [],
    menusChanged: null,
    allergiesSubstitutes: "",
    allergiesSubstitutes_enUS: "",
  });
  const [fileImage, setFileImage] = useState({
    file1: "",
    file2: "",
    file3: "",
    file4: "",
  });
  const [fileName, setFileName] = useState({
    file1: "",
    file2: "",
    file3: "",
    file4: "",
  });
  const [menuList, setMenuList] = useState([]);
  const [oldMenuList, setOldMenuList] = useState([]);
  const [newMenu, setNewMenu] = useState(false);
  const [menuListEnglish, setMenuListEnglish] = useState([]);
  const [newMenuEnglish, setNewMenuEnglish] = useState(false);
  const [googleBusinessDetails, setGoogleBusinessDetails] = useState(null);
  const [tripadvisorLink, setTripadvisorLink] = useState("");
  const [yelpLink, setYelpLink] = useState("");
  const [newProfileFile, setNewProfileFile] = useState(null);
  const [newProfileImagePreview, setNewProfileImagePreview] = useState("");
  const [savedChanges, setSavedChanges] = useState(false);
  const [editLanguage, setEditLanguage] = useState("norwegian");
  const [irrOpeninghours, setIrrOpeningHours] = useState(false);
  const [irrOpeninghoursText, setIrrOpeninghoursText] = useState("");
  const [menuFetchSelectors, setMenuFetchSelectors] = useState({});
  const [menuFetchUrl, setMenuFetchUrl] = useState("");
  const [menuFetchUrlEnglish, setMenuFetchUrlEnglish] = useState("");
  const [menuFetchUrlType, setMenuFetchUrlType] = useState("");
  const [config, setConfig] = useState({
    category_container: "",
    category_name: "",
    product_item: "",
    title: "",
    description: "",
    price: "",
    allergens: "",
  });
  const [menuFetchAvailable, setMenuFetchAvailable] = useState(false);
  const [ignoreTerms, setIgnoreTerms] = useState("");
  const [includeTerms, setIncludeTerms] = useState("");
  const [suggestedTags, setSuggestedTags] = useState([]);

  useEffect(() => {
    const getLocationInformation = async () => {
      const getLocation = doc(db, "lokasjoner", locationId);
      const regularOpeningHoursRef = collection(
        db,
        "lokasjoner",
        locationId,
        "regularOpeningHours",
      );
      const locationInfo = await getDoc(getLocation);

      if (locationInfo.data().owner === currentBusinessID) {
        setBusinessOwner(true);
      }

      const querySnapshot = await getDocs(regularOpeningHoursRef);
      const regularOpeningHoursOpeningArray = {};
      const regularOpeningHoursClosingArray = {};

      setIrrOpeningHours(
        locationInfo.data().irregularHoursBoolean
          ? locationInfo.data().irregularHoursBoolean
          : false,
      );
      setIrrOpeninghoursText(
        locationInfo.data().irregularHoursText
          ? locationInfo.data().irregularHoursText
          : "",
      );

      setTextInformation((prevState) => ({
        ...prevState,
        name: locationInfo.data().locationName,
        tags: locationInfo.data().tags ? locationInfo.data().tags : [],
        tags_enUS: locationInfo.data().tagsLang
          ? locationInfo.data().tagsLang.en_US
            ? locationInfo.data().tagsLang.en_US
            : []
          : [],
        description_enUS: locationInfo.data().aboutBusinessLang
          ? locationInfo.data().aboutBusinessLang.en_US
            ? locationInfo.data().aboutBusinessLang.en_US
            : ""
          : "",
        adress: locationInfo.data().adress ? locationInfo.data().adress : "",
        description: locationInfo.data().aboutBusiness
          ? locationInfo.data().aboutBusiness
          : "",
        websiteLink: locationInfo.data().website
          ? locationInfo.data().website
          : "",
        bookingLink: locationInfo.data().bookingLink
          ? locationInfo.data().bookingLink
          : "",
        phoneLink: locationInfo.data().phoneNumber
          ? locationInfo.data().phoneNumber
          : "",
        locationTags: locationInfo.data().locationTags
          ? locationInfo.data().locationTags
          : [],
        locationTags_enUS: locationInfo.data().locationTagsLang
          ? locationInfo.data().locationTagsLang.en_US
            ? locationInfo.data().locationTagsLang.en_US
            : []
          : [],
        allergiesSubstitutes: locationInfo.data().allergiesSubstitutes
          ? locationInfo.data().allergiesSubstitutes.text
          : "",
        allergiesSubstitutes_enUS: locationInfo.data().allergiesSubstitutesLang
          ? locationInfo.data().allergiesSubstitutesLang.en_US
            ? locationInfo.data().allergiesSubstitutesLang.en_US.text
            : ""
          : "",
        menusChanged: locationInfo.data().menusChanged
          ? locationInfo.data().menusChanged
          : null,
        lang: locationInfo.data().lang ? locationInfo.data().lang : "nb_NO",
        currency: locationInfo.data().currency
          ? locationInfo.data().currency
          : "nok",
      }));
      setYelpLink(locationInfo.data().yelpLink);
      setTripadvisorLink(locationInfo.data().tripadvisorLink);
      if (locationInfo.data().googleNumReviews) {
        setGoogleBusinessDetails({
          user_ratings_total: locationInfo.data().googleNumReviews,
          rating: locationInfo.data().googleRating,
          place_id: locationInfo.data().googleId,
          url: locationInfo.data().googleLink,
        });
      }
      setMenuList(locationInfo.data().menus);
      setOldMenuList(locationInfo.data().menus);
      setMenuListEnglish(
        locationInfo.data().menuListLang
          ? locationInfo.data().menuListLang.en_US
            ? locationInfo.data().menuListLang.en_US
            : []
          : [],
      );
      setNewProfileImage(locationInfo.data().mainProfileImage);
      setIncludeTerms(locationInfo.data().includeTerms || "");
      setIgnoreTerms(locationInfo.data().ignoreTerms || "");
      setMenuFetchUrl(locationInfo.data().menuFetchUrl || "");
      setMenuFetchAvailable(locationInfo.data().menuFetchAvailable || false);
      setMenuFetchUrlType(
        locationInfo.data().menuFetchUrlType
          ? locationInfo.data().menuFetchUrlType
          : "",
      );

      querySnapshot.forEach((docSnap) => {
        const docData = docSnap.data();
        regularOpeningHoursOpeningArray[docSnap.id] = docData.open;
        regularOpeningHoursClosingArray[docSnap.id] = docData.close;
      });

      if (
        locationInfo.data().regularOpeningHours &&
        Object.entries(locationInfo.data().regularOpeningHours) !== undefined &&
        Object.entries(locationInfo.data().regularOpeningHours) !== null
      ) {
        Object.entries(locationInfo.data().regularOpeningHours).forEach(
          (value) => {
            regularOpeningHoursOpeningArray[value[0]] = value[1].open;
            regularOpeningHoursClosingArray[value[0]] = value[1].close;
          },
        );
      }

      setRegularOpeningHours(regularOpeningHoursOpeningArray);
      setRegularClosingHours(regularOpeningHoursClosingArray);

      setLocationinformation(locationInfo.data());
      if (locationInfo.data().profileImages) {
        locationInfo.data().profileImages.forEach((x, index) => {
          const fname = `file${index + 1}`;
          setFileImage((prevData) => ({
            ...prevData,
            [fname]: x,
          }));
        });
      }
    };
    getLocationInformation();
  }, [locationId, currentBusinessID]);

  async function getMainProfileImageUrl({
    newProfileImage,
    newProfileFile,
    existingImage,
  }) {
    if (newProfileImage !== existingImage && newProfileFile) {
      const uniqueKey = uuidv4();
      const storageRef = ref(getStorage(), `images/${uniqueKey}`);

      const snapshot = await uploadBytes(storageRef, newProfileFile);
      const downloadURL = await getDownloadURL(snapshot.ref);

      return downloadURL;
    }

    if (typeof newProfileImage === "string" && newProfileImage.length > 0) {
      return newProfileImage;
    }

    return undefined;
  }

  const onSaveChanges = async () => {
    let averagePrice = 0;
    let totalPrice = 0;
    let numItems = 0;
    let overAllRating = 0;
    let ratingCount = 0;

    menuList.forEach((category) => {
      category.prices.forEach((price, index) => {
        if (typeof price === "number" && !isNaN(price)) {
          totalPrice += price;
          numItems += 1;
        } else {
          const variants = category.variants?.[index];
          if (Array.isArray(variants)) {
            variants.forEach((variant) => {
              if (
                variant &&
                typeof variant.price === "number" &&
                !isNaN(variant.price)
              ) {
                totalPrice += variant.price;
                numItems += 1;
              }
            });
          }
        }
      });
    });

    averagePrice = numItems > 0 ? totalPrice / numItems : 0;

    if (
      fileImage.file1.length > 0 ||
      fileImage.file2.length > 0 ||
      fileImage.file3.length > 0 ||
      fileImage.file4.length > 0 ||
      locationInformation.profileImages !== undefined
    ) {
      let profileImages = ["", "", "", ""];
      if (locationInformation.profileImages !== undefined) {
        profileImages = locationInformation.profileImages;
      }

      const uploadPromises = profileImages.map(async (x, index) => {
        const file = `file${index + 1}`;

        if (fileImage[file] === "" && profileImages[index] !== "") {
          profileImages[index] = "";
          return { index, downloadURL: "" };
        }

        if (fileImage[file] !== x && fileName[file] instanceof File) {
          const uniqueKey = uuidv4();
          const storageRef = ref(storage, `images/${uniqueKey}`);

          try {
            const snapshot = await uploadBytes(storageRef, fileName[file]);
            const downloadURL = await getDownloadURL(snapshot.ref);
            return { index, downloadURL };
          } catch (error) {
            console.error("Upload error:", error);
            return null;
          }
        }
        return null;
      });

      Promise.all(uploadPromises).then((results) => {
        const successfulUploads = results.filter((result) => result !== null);

        if (successfulUploads.length > 0) {
          successfulUploads.forEach(({ index, downloadURL }) => {
            profileImages[index] = downloadURL;
          });

          const collectionRef = doc(db, "lokasjoner", locationId);
          updateDoc(collectionRef, { profileImages }).catch((error) => {
            console.error("Error updating document: ", error);
          });
        } else if (Object.keys(fileImage).length > 0) {
          const collectionRef = doc(db, "lokasjoner", locationId);
          let newProfileImageArray = [];
          Object.keys(fileImage).forEach((key) => {
            newProfileImageArray.push(fileImage[key]);
          });
          updateDoc(collectionRef, {
            profileImages: newProfileImageArray,
          }).catch((error) => {
            console.error("Error updating document: ", error);
          });
        }
      });
    }

    // Save Tripadvisor
    if (tripadvisorLink && typeof tripadvisorLink === "string") {
      const match = tripadvisorLink.match(/-d(\d+)-/);

      if (match) {
        const id = match[1];
        await fetch(
          `https://us-central1-menuapp-4c413.cloudfunctions.net/tripadvisor/${id}`,
        )
          .then((res) => res.json())
          .then((data) => {
            if (!data?.businessDetailsData) {
              console.error("Tripadvisor lookup failed, skipping:", data);
              return;
            }
            const uniqueKey = uuidv4();
            const docRef = doc(db, "lokasjoner", locationId);

            const subcollectionRef = collection(docRef, "tripAdvisorRatings");
            const dayDocumentRef = doc(subcollectionRef, uniqueKey);

            updateDoc(docRef, {
              tripadvisorRating: parseFloat(data.businessDetailsData.rating),
              tripadvisorLastUpdate: new Date(),
              tripadvisorId: id,
              tripadvisorNumReviews: parseFloat(
                data.businessDetailsData.num_reviews,
              ),
              tripadvisorLink: tripadvisorLink,
              tripadvisorRatingImageLink:
                data.businessDetailsData.rating_image_url,
            });
            const r = parseFloat(data.businessDetailsData.rating);
            if (!isNaN(r)) {
              overAllRating += r;
              ratingCount++;
            }
            setDoc(dayDocumentRef, {
              reviews: data.reviewData.data,
              date: new Date(),
            }).catch((error) => {
              console.error(`Error updating ${uniqueKey} hours:`, error);
            });
          })
          .catch((error) => {
            console.error(error);
          });
      }
    }

    // Save Yelp
    var baseUrl = "https://www.yelp.com/biz/";
    if (yelpLink && typeof yelpLink === "string") {
      var filteredUrl = yelpLink.replace(baseUrl, "");

      await fetch(
        `https://us-central1-menuapp-4c413.cloudfunctions.net/tripadvisor/yelpDetails/${filteredUrl}`,
      )
        .then((res) => res.json())
        .then((data) => {
          if (!data?.businessDetailsData) {
            console.error("Yelp lookup failed, skipping:", data);
            return;
          }
          const uniqueKey = uuidv4();
          const docRef = doc(db, "lokasjoner", locationId);

          const subcollectionRef = collection(docRef, "yelpRatings");
          const dayDocumentRef = doc(subcollectionRef, uniqueKey);

          updateDoc(docRef, {
            yelpRating: parseFloat(data.businessDetailsData.rating),
            yelpLastUpdate: new Date(),
            yelpId: data.businessDetailsData.id,
            yelpLink: yelpLink,
            yelpNumReviews: data.businessDetailsData.review_count,
          });
          const r = parseFloat(data.businessDetailsData.rating);
          if (!isNaN(r)) {
            overAllRating += r;
            ratingCount++;
          }
          setDoc(dayDocumentRef, {
            reviews: data.reviewData?.reviews,
            date: new Date(),
          }).catch((error) => {
            console.error(`Error updating ${uniqueKey} hours:`, error);
          });
        })
        .catch((error) => {
          console.error("Yelp fetch failed:", error);
        });
    }

    // Save regular opening hours
    if (
      regularOpeningHours.mon !== null ||
      regularOpeningHours.tue !== null ||
      regularOpeningHours.wed !== null ||
      regularOpeningHours.thu !== null ||
      regularOpeningHours.fri !== null ||
      regularOpeningHours.sat !== null ||
      regularOpeningHours.sun !== null
    ) {
      const parentDocRef = doc(db, "lokasjoner", locationId);
      const openingHoursObject = {};

      for (const dayOfWeek in regularOpeningHours) {
        if (Object.hasOwnProperty.call(regularOpeningHours, dayOfWeek)) {
          const openingHour = regularOpeningHours[dayOfWeek];
          const closingHour = regularClosingHours[dayOfWeek];

          openingHoursObject[dayOfWeek] = {
            open: openingHour,
            close: closingHour,
          };
        }
      }

      setDoc(
        parentDocRef,
        { regularOpeningHours: openingHoursObject },
        { merge: true },
      ).catch((error) => {
        console.error("Error updating opening hours:", error);
      });
    }

    if (
      (googleBusinessDetails !== null &&
        googleBusinessDetails.rating !== undefined) ||
      null
    ) {
      const r = parseFloat(googleBusinessDetails?.rating);
      if (!isNaN(r)) {
        overAllRating += r;
        ratingCount++;
      }
    }

    const bookingAvailable =
      textInformation.bookingLink.length > 0 ||
      textInformation.phoneLink.length > 0
        ? true
        : false;

    if (ratingCount > 0) {
      overAllRating = overAllRating / ratingCount;
    }

    overAllRating = Math.min(5, overAllRating);
    overAllRating = Math.round(overAllRating * 10) / 10;

    const lang_language = normalizeLang(textInformation.lang) || "nb_NO";
    const tagListTags = textInformation.tags || [];

    const aboutBusiness = textInformation.description || "";
    const toReview = locationInformation.toReview || false;
    const aboutReserve =
      locationInformation.type === "restaurant"
        ? "Reserver bord på telefon eller på nettsiden vår!"
        : "Bestill billetter på telefon eller på nettsiden vår!";
    const googleNumReviews = googleBusinessDetails?.user_ratings_total || 0;
    const googleRating = googleBusinessDetails?.rating || 0;
    const googleId = googleBusinessDetails?.place_id || "";
    const googleLink = googleBusinessDetails?.url || "";
    const lang = textInformation.lang ? textInformation.lang : "nb_NO";
    const currency = textInformation.currency
      ? textInformation.currency
      : "nok";
    const locationName = textInformation.name || "";
    const aboutBusinessLang = {
      en_US: textInformation.description_enUS || "",
      [lang]: textInformation.description || "",
    };
    const menuListLang = {
      en_US: menuListEnglish || [],
      [lang]: menuList,
    };
    const locationTagsLang = await buildTagsLang({
      tagList: textInformation.locationTags || [],
      fromLang: lang,
      translate: translateBatch,
      existing: { en_US: textInformation.locationTags_enUS },
    });
    const menus = menuList || [];
    const phoneNumber =
      textInformation.phoneLink !== undefined ? textInformation.phoneLink : "";
    const tags = textInformation.tags || [];
    const tagsLang = await buildTagsLang({
      tagList: tagListTags,
      fromLang: lang_language,
      translate: translateBatch,
      existing: {
        en_US: textInformation.tags_enUS,
      },
    });
    const website =
      textInformation.websiteLink !== undefined
        ? textInformation.websiteLink
        : "";
    const bookingLink =
      textInformation.bookingLink !== undefined
        ? textInformation.bookingLink
        : "";
    const adress =
      textInformation.adress !== undefined ? textInformation.adress : "";
    const locationTags = textInformation.locationTags || [];
    const menusChanged =
      JSON.stringify(menus) === JSON.stringify(oldMenuList)
        ? textInformation.menusChanged
        : new Date().getTime();
    const irregularHoursBoolean = irrOpeninghours ? irrOpeninghours : false;
    const irregularHoursText = irrOpeninghoursText;
    const allergiesSubstitutes =
      textInformation.allergiesSubstitutes.length > 0
        ? { text: textInformation.allergiesSubstitutes, publisher: "Eier" }
        : null;
    const allergiesSubstitutesLang =
      textInformation.allergiesSubstitutes.length > 0
        ? {
            [lang]: {
              text: textInformation.allergiesSubstitutes,
              publisher: "Eier",
            },
            en_US: {
              text: textInformation.allergiesSubstitutes_enUS,
              publisher: "Owner",
            },
          }
        : null;

    const mapping =
      menuFetchUrlType === "api" || menuFetchUrlType === "multipleApi"
        ? {
            categoriesPath: "$" + config.category_container + "[*]",
            categoryNamePath: "$" + config.category_name,
            itemsPath: "$" + config.product_item + "[*]",
            item: {
              namePath: "$" + config.title,
              descriptionPath: "$" + config.description,
              pricePath: "$" + config.price,
              allergensPath: "$" + config.allergens,
            },
          }
        : {};

    const upLoadedMenuFetchUrl = menuFetchUrl;
    const upLoadedMenuFetchUrlEnglish =
      menuFetchUrlType !== "multipleApi" ? menuFetchUrlEnglish : "";
    const upLoadedMenuFetchSelectors = menuFetchAvailable
      ? menuFetchUrlType === "selector"
        ? config
        : {}
      : {};

    const apiMapping = menuFetchAvailable
      ? menuFetchUrlType === "api" || menuFetchUrlType === "multipleApi"
        ? mapping
        : {}
      : {};

    const multipleApiMappingidObjects =
      menuFetchAvailable && menuFetchUrlType === "multipleApi"
        ? ignoreTerms
        : "";

    const multipleApiURL = menuFetchAvailable
      ? menuFetchUrlType === "multipleApi"
        ? menuFetchUrlEnglish
        : ""
      : "";

    const ignoreTermsArray = ignoreTerms
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);

    const includeTermsArray = includeTerms
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);

    const dbCollection = doc(db, "lokasjoner", locationId);
    const dbCollectionList = doc(db, "lokasjoner_list", locationId);
    const dbCollectionMap = doc(db, "lokasjoner_map", locationId);

    const resolvedMainProfileImage = await getMainProfileImageUrl({
      newProfileImage,
      newProfileFile,
      existingImage: locationInformation.mainProfileImage,
    });

    if (resolvedMainProfileImage) {
      await updateDoc(dbCollection, {
        mainProfileImage: resolvedMainProfileImage,
      });
    }

    await updateDoc(dbCollection, {
      aboutBusiness,
      aboutReserve,
      averagePrice,
      googleNumReviews,
      googleRating,
      googleId,
      googleLink,
      overAllRating,
      lang,
      currency,
      locationName,
      aboutBusinessLang,
      menuListLang,
      locationTagsLang,
      menus,
      menusChanged,
      phoneNumber,
      tags,
      tagsLang,
      website,
      bookingLink,
      adress,
      bookingAvailable,
      locationTags,
      toReview,
      irregularHoursBoolean,
      irregularHoursText,
      allergiesSubstitutes,
      allergiesSubstitutesLang,
      menuFetchSelectors: upLoadedMenuFetchSelectors,
      menuFetchUrl: upLoadedMenuFetchUrl,
      menuFetchAvailable: menuFetchAvailable,
      menuFetchUrlEnglish: upLoadedMenuFetchUrlEnglish,
      menuFetchUrlType,
      apiMapping: apiMapping,
      multipleApiMappingidObjects: multipleApiMappingidObjects,
      multipleApiURL: multipleApiURL,
      ignoreTerms,
      includeTerms,
      includeTermsArray,
      ignoreTermsArray,
    }).catch((error) => {
      console.error("Error adding document: ", error);
    });

    await setDoc(dbCollectionMap, {
      active: locationInformation.active,
      locationName,
      ...(locationInformation.slug && { slug: locationInformation.slug }),
      lat: locationInformation.lat,
      lng: locationInformation.lng,
      secondaryType: locationInformation.secondaryType
        ? locationInformation.secondaryType
        : "",
      type: locationInformation.type,
      tags,
      tagsLang,
    }).catch((error) => {
      console.error("Error adding documentmap: ", error);
    });

    await setDoc(dbCollectionList, {
      aboutBusinessLang,
      active: locationInformation.active,
      averagePrice,
      currency,
      g: locationInformation.g ? locationInformation.g : "",
      lat: locationInformation.lat,
      lng: locationInformation.lng,
      lang: locationInformation.lang,
      locationName,
      locationTagsLang,
      ...(resolvedMainProfileImage && {
        mainProfileImage: resolvedMainProfileImage,
      }),
      mapImage: locationInformation.mapImage
        ? locationInformation.mapImage
        : "",
      regularOpeningHours: locationInformation.regularOpeningHours,
      overAllRating,
      ...(locationInformation.slug && { slug: locationInformation.slug }),
      type: locationInformation.type,
      secondaryType: locationInformation.secondaryType
        ? locationInformation.secondaryType
        : "",
      tags,
      tagsLang,
    }).catch((error) => {
      console.error("Error adding documentlist: ", error);
    });

    setSavedChanges(true);
  };

  const onActivateLocation = async () => {
    const dbCollection = doc(db, "lokasjoner", locationId);
    const dbCollectionList = doc(db, "lokasjoner_list", locationId);
    const dbCollectionMap = doc(db, "lokasjoner_map", locationId);
    const businessCollection = doc(db, "businesses", currentBusinessID);
    const businessInformation = await getDoc(businessCollection);
    const stripeID = businessInformation.data().stripeID;
    const activeSub = businessInformation.data().activeSub;

    if (activeSub) {
      const subscription = await stripe.subscriptions.list({
        customer: stripeID,
        status: "active",
      });

      if (subscription.data[0] !== undefined) {
        const numActive = subscription.data[0].quantity;
        await stripe.subscriptions.update(subscription.data[0].id, {
          quantity: numActive + 1,
        });
      }
      updateDoc(dbCollection, {
        activeSub: true,
      });
    }

    await updateDoc(dbCollection, {
      active: true,
    });
    updateDoc(dbCollectionMap, { active: true }).catch(() => {});
    updateDoc(dbCollectionList, { active: true }).catch(() => {});
    setLocationinformation({ ...locationInformation, active: true });
  };

  const onDeactivateLocation = async () => {
    const dbCollection = doc(db, "lokasjoner", locationId);
    const dbCollectionList = doc(db, "lokasjoner_list", locationId);
    const dbCollectionMap = doc(db, "lokasjoner_map", locationId);
    const businessCollection = doc(db, "businesses", currentBusinessID);
    const businessInformation = await getDoc(businessCollection);
    const stripeID = businessInformation.data().stripeID;
    const activeSub = businessInformation.data().activeSub;

    if (activeSub) {
      const subscription = await stripe.subscriptions.list({
        customer: stripeID,
        status: "active",
      });

      if (subscription.data[0] !== undefined) {
        const numActive = subscription.data[0].quantity;
        await stripe.subscriptions.update(subscription.data[0].id, {
          quantity: numActive - 1,
        });
      }
    }

    updateDoc(dbCollection, {
      active: false,
    });
    updateDoc(dbCollectionMap, { active: false }).catch(() => {});
    updateDoc(dbCollectionList, { active: false }).catch(() => {});
    setLocationinformation({ ...locationInformation, active: false });
  };

  return {
    loading: locationInformation === null,
    locationInformation,
    setLocationinformation,
    businessOwner,
    regularOpeningHours,
    setRegularOpeningHours,
    regularClosingHours,
    setRegularClosingHours,
    newCustomOpeningHours,
    setNewCustomOpeningHours,
    newProfileImage,
    setNewProfileImage,
    textInformation,
    setTextInformation,
    fileImage,
    setFileImage,
    fileName,
    setFileName,
    menuList,
    setMenuList,
    newMenu,
    setNewMenu,
    menuListEnglish,
    setMenuListEnglish,
    newMenuEnglish,
    setNewMenuEnglish,
    googleBusinessDetails,
    setGoogleBusinessDetails,
    tripadvisorLink,
    setTripadvisorLink,
    yelpLink,
    setYelpLink,
    newProfileFile,
    setNewProfileFile,
    newProfileImagePreview,
    setNewProfileImagePreview,
    savedChanges,
    setSavedChanges,
    editLanguage,
    setEditLanguage,
    irrOpeninghours,
    setIrrOpeningHours,
    irrOpeninghoursText,
    setIrrOpeninghoursText,
    menuFetchSelectors,
    setMenuFetchSelectors,
    menuFetchUrl,
    setMenuFetchUrl,
    menuFetchUrlEnglish,
    setMenuFetchUrlEnglish,
    menuFetchUrlType,
    setMenuFetchUrlType,
    config,
    setConfig,
    menuFetchAvailable,
    setMenuFetchAvailable,
    ignoreTerms,
    setIgnoreTerms,
    includeTerms,
    setIncludeTerms,
    suggestedTags,
    setSuggestedTags,
    onSaveChanges,
    onActivateLocation,
    onDeactivateLocation,
  };
}
